import { z } from 'zod';
// ----------------------------------------------------------------------------
//  IBE CMS config — the source of truth for region/city → property mappings.
//
//  This is the same config the IBE (hilton/booking-engine) frontend reads: it
//  is fetched from `GET {IBE_API_URL}/config` (ibe-api `getConfig`, backed by
//  the IBE CMS). We parse only the subset the Sales Desk needs; `passthrough()`
//  lets the many unused CMS fields flow through untouched. Nothing about the
//  region/city → property mapping is hardcoded here — it all comes from
//  `properties[].location` and `properties[].regions`, mirroring the IBE
//  resolvers in apps/ibe-frontend/src/services/utils.ts.
// ----------------------------------------------------------------------------
export const CmsPropertySchema = z
    .object({
    // Migration window: the CMS is moving from `propertyApaleoId` to
    // `propertyId`. Both are accepted (optional) while the cutover is in flight;
    // once complete, the Apaleo variant will be removed. Read via `cmsPropertyId`
    // so call sites don't have to know which key a given config carries.
    propertyId: z.string().optional(),
    propertyApaleoId: z.string().optional(),
    name: z.string(),
    // `location` is the IBE CMS city/market label (e.g. "Washington, DC").
    location: z.string().optional(),
    regions: z.array(z.string()).optional(),
    isEnabled: z.boolean().optional()
})
    .passthrough();
// During the migration a property may carry either key. Prefer the new
// `propertyId`, fall back to the legacy `propertyApaleoId`.
export const cmsPropertyId = (property) => property.propertyId ?? property.propertyApaleoId;
// A CMS image asset: `image` is the URL, `altText` the accessible description.
export const CmsImageAssetSchema = z
    .object({
    image: z.string(),
    // The CMS emits an explicit `null` for images without alt text; accept it
    // and normalize to `undefined` (see `nullishString` below).
    altText: z
        .string()
        .nullish()
        .transform(value => value ?? undefined)
})
    .passthrough();
// The CMS emits an explicit `null` (not just an absent key) for unset room-type
// fields. `.optional()` only accepts `undefined`, so parse null as well and
// normalize both to `undefined` — this keeps the inferred types free of `null`
// and lets the modal's optional chaining and `?? []` fallbacks stand.
const nullishString = z
    .string()
    .nullish()
    .transform(value => value ?? undefined);
const nullishStringArray = z
    .array(z.string())
    .nullish()
    .transform(value => value ?? undefined);
// The rich per-room-type detail the "View details" modal renders. This is the
// same `unitGroups` block the IBE frontend reads from the shared CMS config,
// keyed by `unitgroupApaleoId` — which is the Apaleo unit-group id the offers
// carry as the room type id. Every field is optional/loose so a thin or evolving
// CMS record never breaks the config parse.
export const CmsUnitGroupSchema = z
    .object({
    // Same migration window as properties: `unitGroupId`/`propertyId` are the
    // new keys, `unitgroupApaleoId`/`propertyApaleoId` the legacy ones. Read via
    // `cmsUnitGroupId` so call sites stay agnostic to which key is present.
    unitGroupId: z.string().optional(),
    unitgroupApaleoId: z.string().optional(),
    propertyId: z.string().optional(),
    propertyApaleoId: z.string().optional(),
    name: z.string().optional(),
    description: nullishString,
    images: z
        .array(CmsImageAssetSchema)
        .nullish()
        .transform(value => value ?? undefined),
    attributes: z
        .object({
        bedrooms: z.number().optional(),
        bathrooms: z.number().optional(),
        maxGuests: z.number().optional(),
        unitSize: z
            .string()
            .nullish()
            .transform(value => value ?? undefined)
    })
        .passthrough()
        .nullish()
        .transform(value => value ?? undefined),
    basicFeatures: nullishStringArray,
    sleepingFeatures: nullishStringArray,
    diningFeatures: nullishStringArray,
    workingFeatures: nullishStringArray,
    familyFeatures: nullishStringArray,
    outdoorFeatures: nullishStringArray,
    safetyFeatures: nullishStringArray,
    accessibilityFeatures: nullishStringArray
})
    .passthrough();
// Prefer the new `unitGroupId`, fall back to the legacy `unitgroupApaleoId`.
export const cmsUnitGroupId = (group) => group.unitGroupId ?? group.unitgroupApaleoId;
export const CmsConfigSchema = z.object({
    properties: z.array(CmsPropertySchema),
    locations: z.array(z.string()),
    regions: z.array(z.string()),
    // Optional so older configs and fixtures without room detail still parse; the
    // modal treats a missing entry as "no details available".
    unitGroups: z.array(CmsUnitGroupSchema).optional().default([])
});
// Looks up a room type's CMS detail by its Apaleo unit-group id (the offer's
// room type id). Returns undefined when the CMS has no matching record.
export const findUnitGroup = (config, unitGroupId) => config?.unitGroups.find(group => cmsUnitGroupId(group) === unitGroupId);
// Looks up a property's CMS record by its Apaleo id — used for the location
// shown under the room name in the details modal.
export const findCmsProperty = (config, propertyId) => config?.properties.find(property => cmsPropertyId(property) === propertyId);
